import React, {useState} from 'react';
import {Button, Card, Form, Spinner} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTimes} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from 'react-i18next';
import {Amplify} from 'aws-amplify';
import {useRecordEvent} from 'aws-rum-react';
import UploadedFilesList from './CardFields/UploadedFilesList';
import ItemMPSelect from './CardFields/ItemMPSelect';
import ManufacturersSelect from './CardFields/ManufacturersSelect';
import ExtractOptionsSelect from './CardFields/ExtractOptionsSelect';
import ThresholdSettings from './CardFields/ThresholdSettings';
import TopKResultSettings from './CardFields/TopKResultSettings';
import aws_config from '../../modified_aws-export';
import {FetchOptionType, IOptionType} from "../../types/common";
import {IManualFinderFilterModel, IManualFinderModel} from "../../types/manualFinder";
import {specialToolPayloadBuilder} from "../../utils/modelBuilder";
import {MultiValue, SingleValue} from "react-select";
import {ExtractedItem} from "../../utils/manualFinderUtils";
import {extractToolAndManualsFromPDF} from "../../services/manualFinder/extractToolAndManualsFromPDF";
import {useLoadOptions} from "../../hooks/useLoadOptions";

Amplify.configure(aws_config);

interface IPDFUploadedCardProps {
    onClearFileUploaded: () => void;
    manualFinderFilterProps: IManualFinderFilterModel;
    fileMap: Record<string, string>;
    setManualFinderProps: (manualFinderProps: IManualFinderModel) => void;
    setExtractionFinished: (status: boolean) => void;
    onValueChange: (value: number | SingleValue<IOptionType> | MultiValue<IOptionType> | boolean[], key: string) => void
    uploadedFiles: File[],
    selectedExtractedType: IOptionType,
    setSelectedExtractedType: (selectedExtractedType: SingleValue<IOptionType>) => void
}

export const PDFUploadedCard: React.FC<IPDFUploadedCardProps> = ({
    onClearFileUploaded,
    manualFinderFilterProps,
    fileMap,
    setManualFinderProps,
    setExtractionFinished,
    onValueChange,
    uploadedFiles,
    selectedExtractedType,
    setSelectedExtractedType
}) => {
    const { t } = useTranslation();
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const recordEvent = useRecordEvent();
    const {
        manufacturerOptions,
        extractedTypeOptions,
        itemMPOptions
    } = useLoadOptions([FetchOptionType.Manufacturer, FetchOptionType.ItemMPOptions]);
    
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoadingSubmit(true);
        try {
            const payload = specialToolPayloadBuilder(manualFinderFilterProps, fileMap)
            const startTime = performance.now();
            const manualFinderProps = await extractToolAndManualsFromPDF(payload);
            recordEvent("SpecialToolsExtractionFinished", {
                duration: `${(performance.now() - startTime) / 1000}s`,
            });
            onValueChange(manualFinderProps.specialTools.map(value => true), 'checkedTools')
            setManualFinderProps(manualFinderProps);
            setExtractionFinished(true);
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsLoadingSubmit(false);
        }
    };

    return (
        <Card className="upload-pdf-card">
            <Card.Body>
                <Form onSubmit={handleSubmit} className="d-flex flex-column upload-form">
                    <UploadedFilesList title={t("PdfFileUpload.UploadedFiles")}
                        uploadedFiles={uploadedFiles}
                        noFilesText={t('PdfFileUpload.NoFiles')} />

                    <ItemMPSelect
                        options={itemMPOptions}
                        selectedItem={manualFinderFilterProps.selectedItemMP}
                        isDisabled={isLoadingSubmit}
                        onChange={(value) => onValueChange(value, 'selectedItemMP')}
                        placeholder={t('PdfFileUpload.ItemMPPlaceholder')}
                        tooltipContent={t('PdfFileUpload.ToolTipText')}
                    />

                    <ManufacturersSelect
                        title={t("Filter.manufacturer")}
                        options={manufacturerOptions}
                        selectedItems={manualFinderFilterProps.selectedManufacturers}
                        isDisabled={selectedExtractedType.value === ExtractedItem.SpecialTools || isLoadingSubmit}
                        onChange={(values) => onValueChange(values, 'selectedManufacturers')}
                        placeholder={t('Filter.manufacturer')}
                        tooltipContent={t('ExtractedImages.ManufacturerDisabledTooltip')}
                    />

                    <ExtractOptionsSelect
                        title={t("Sidebar.Info")}
                        options={extractedTypeOptions}
                        selectedOption={selectedExtractedType}
                        isDisabled={isLoadingSubmit}
                        onChange={setSelectedExtractedType}
                    />

                    {selectedExtractedType.value === ExtractedItem.SpecialTools && (
                        <TopKResultSettings
                            title={t("PdfFileUpload.TopKResults")}
                            topK={manualFinderFilterProps.topKSpecialTools}
                            isDisabled={isLoadingSubmit}
                            onTopKChange={(value) => onValueChange(value, 'topKSpecialTools')}
                        />
                    )}
                    {selectedExtractedType.value === ExtractedItem.ImagesAutomatic && (
                        <>
                            <ThresholdSettings
                                threshold={manualFinderFilterProps.threshold}
                                isDisabled={isLoadingSubmit}
                                onThresholdChange={(value) => onValueChange(value, 'threshold')}
                            />
                            <TopKResultSettings
                                title={t("PdfFileUpload.TopKResults")}
                                topK={manualFinderFilterProps.maxNumberOfManuals}
                                isDisabled={isLoadingSubmit}
                                onTopKChange={(value) => onValueChange(value, 'maxNumberOfManuals')}
                            />
                        </>
                    )}
                    <div className="text-center">
                        <Button type="submit" variant="secondary" disabled={isLoadingSubmit}>
                            {isLoadingSubmit ? (
                                <>
                                    <Spinner animation="border" size="sm" />
                                    {' '}{t('DropZone.loading')}
                                </>
                            ) : (
                                t('Button.extractSpecialTools')
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
            <button onClick={onClearFileUploaded} className="close-button" disabled={isLoadingSubmit}>
                <FontAwesomeIcon icon={faTimes} />
            </button>
        </Card>
    );
};
